// Resolve entity UUIDs to human names for display. The dashboard references entities by id in many
// places (a task's network, a container's publication, …); showing the raw UUID is unreadable. This
// hook fetches the small lookup lists once and returns resolvers that fall back to a shortened id when
// the entity isn't loaded (so a stale/foreign id still renders something sane, never a crash).
import { useMemo } from 'react';
import { api } from '../api/client';
import { useResource } from './useResource';

/** Shorten a UUID for fallback display: "1bd201c6…". */
export function shortId(id: string): string {
  return id.length > 10 ? `${id.slice(0, 8)}…` : id;
}

export interface Names {
  networkName: (id?: string | null) => string;
  publicationLabel: (id?: string | null) => string;
  /** The service TEMPLATE id (service_definition_id) backing a publication, if known. */
  publicationServiceDefId: (id?: string | null) => string | undefined;
  loading: boolean;
}

export function useNames(): Names {
  const nets = useResource(() => api.networks(), []);
  const pubs = useResource(() => api.publications(), []);

  const netById = useMemo(
    () => new Map((nets.data?.items ?? []).map((n) => [n.id, n])),
    [nets.data],
  );
  const pubById = useMemo(
    () => new Map((pubs.data?.items ?? []).map((p) => [p.id, p])),
    [pubs.data],
  );

  return useMemo(
    () => ({
      networkName: (id) => {
        if (!id) return '';
        const n = netById.get(id);
        return n ? n.name || n.slug : shortId(id);
      },
      publicationLabel: (id) => {
        if (!id) return '';
        const p = pubById.get(id);
        if (!p) return shortId(id);
        // service_slug is the network-unique handle; prefer "<slug> · <service name>" when both exist.
        return p.service_name ? `${p.service_slug} · ${p.service_name}` : p.service_slug;
      },
      publicationServiceDefId: (id) => (id ? pubById.get(id)?.service_definition_id : undefined),
      loading: nets.loading || pubs.loading,
    }),
    [netById, pubById, nets.loading, pubs.loading],
  );
}
